/**
 * API error handling utilities
 */
import { ERROR_MESSAGES, HTTP_STATUS } from '../constants';
import type { ApiError, ApiErrorResponseBody, ValidationErrors } from '../types';

const TECHNICAL_ERROR_PATTERN =
  /sql|database|dberror|sqlite|postgres|npgsql|mysql|oracle|deadlock|stack trace|exception|inner exception|object reference|nullreference|timeout expired|connection (pool|was closed|reset|forcibly)|cannot insert|violation of|system\.(data|invalidoperation|argument)|axioserror|request failed with status|econnreset|enotfound|enetunreach|socket hang up|i\/o error|at [a-z0-9_$.]+\(/i;

/**
 * Thrown when a request answered but carried nothing usable — a missing payload
 * or `success: false`. List endpoints must raise this instead of returning an
 * empty page, or the screen presents a failure as "nothing here yet".
 *
 * The `message` is generic and safe to display directly; the `endpoint` property
 * is kept separately for logging. `getUserFacingErrorMessage` returns the
 * caller's own fallback copy, but if a consumer ever reads `Error.message`
 * directly it will not leak internal endpoint names.
 */
class UnusableResponseError extends Error {
  readonly endpoint: string;

  constructor(endpoint: string) {
    super('The server response was not usable');
    this.name = 'UnusableResponseError';
    this.endpoint = endpoint;
  }
}

/**
 * True when a backend message is a technical/database/exception dump, not UX copy.
 */
const isTechnicalServerMessage = (message: string): boolean => {
  const trimmed = message.trim();
  if (!trimmed) {
    return true;
  }
  if (trimmed.length > 180) {
    return true;
  }
  if (trimmed.includes('\n') && trimmed.length > 80) {
    return true;
  }
  return TECHNICAL_ERROR_PATTERN.test(trimmed);
};

const userSafeServerMessage = (
  serverMessage: string | null | undefined,
  fallback: string,
): string => {
  if (!serverMessage) {
    return fallback;
  }
  if (isTechnicalServerMessage(serverMessage)) {
    return fallback;
  }
  return serverMessage;
};

/**
 * Extract a user-friendly message from an Axios-style error.
 */
const getApiErrorMessage = (error: ApiError): string => {
  // Network error (no response)
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return ERROR_MESSAGES.TIMEOUT_ERROR;
    }
    return ERROR_MESSAGES.NETWORK_ERROR;
  }

  const { status, data } = error.response;
  const body: ApiErrorResponseBody | undefined =
    data && typeof data === 'object' ? data : undefined;

  // Try to pull message from response body
  const serverMessage =
    body?.message ||
    body?.error ||
    body?.detail ||
    (typeof data === 'string' ? data : null);

  switch (status) {
    case HTTP_STATUS.BAD_REQUEST:
      return userSafeServerMessage(
        serverMessage,
        'Invalid request. Please check your input.',
      );
    case HTTP_STATUS.UNAUTHORIZED:
      return userSafeServerMessage(serverMessage, ERROR_MESSAGES.UNAUTHORIZED);
    case HTTP_STATUS.FORBIDDEN:
      return userSafeServerMessage(
        serverMessage,
        'You do not have permission to perform this action.',
      );
    case HTTP_STATUS.NOT_FOUND:
      return userSafeServerMessage(
        serverMessage,
        'The requested resource was not found.',
      );
    case HTTP_STATUS.INTERNAL_SERVER_ERROR:
      return userSafeServerMessage(serverMessage, ERROR_MESSAGES.SERVER_ERROR);
    default:
      return userSafeServerMessage(serverMessage, ERROR_MESSAGES.SERVER_ERROR);
  }
};

/**
 * User-facing copy for any thrown value — Axios errors go through
 * `getApiErrorMessage`; local Error messages are kept unless they look technical.
 */
const getUserFacingErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof UnusableResponseError) {
    return fallback;
  }
  const apiLike = error as ApiError;
  if (apiLike?.response || apiLike?.code) {
    return getApiErrorMessage(apiLike);
  }
  if (error instanceof Error && error.message) {
    return isTechnicalServerMessage(error.message) ? fallback : error.message;
  }
  return fallback;
};

/**
 * Parse validation errors from a 422 or 400 response.
 */
const parseValidationErrors = (error: ApiError): ValidationErrors => {
  const errors: ValidationErrors = {};
  if (!error.response) return errors;

  const { data } = error.response;
  const body: ApiErrorResponseBody | undefined =
    data && typeof data === 'object' ? data : undefined;

  if (body?.errors && typeof body.errors === 'object') {
    Object.keys(body.errors).forEach(key => {
      const messages = body.errors?.[key];
      errors[key] = Array.isArray(messages) ? messages[0] : messages ?? '';
    });
  }
  return errors;
};

export type { ApiError, ApiErrorResponseBody, ValidationErrors };

export {
  getApiErrorMessage,
  getUserFacingErrorMessage,
  parseValidationErrors,
  UnusableResponseError,
};
